import { LogOut, Shield, User, Zap } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useLocalMode } from '@/hooks/useLocalMode';

export function NavigationBar() {
  const { user, hasAnyRole, logout } = useAuth();
  const { isLocalAvailable } = useLocalMode();

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      isActive
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
    }`;

  const canAccessAdmin = hasAnyRole(['admin', 'superadmin']);
  const canAccessConfig = isLocalAvailable && user?.hasActiveSubscription;

  return (
    <nav className="h-14 border-b border-border px-4 flex items-center justify-between bg-card">
      {/* Logo/Brand and Main Navigation */}
      <div className="flex items-center space-x-6">
        <div className="flex items-center space-x-2">
          <img
            src="/logo.svg"
            alt="SafeIdea.ai"
            className="h-8 w-8 bg-white dark:bg-white rounded"
          />
          <span className="font-semibold">SafeIdea</span>
        </div>

        {/* Main Navigation */}
        <div className="flex items-center space-x-1">
          <NavLink to="/ai" className={navLinkClass}>
            AI Assistant
          </NavLink>
          {/* Temporarily hidden - not fully working yet */}
          {/* <NavLink to="/patents" className={navLinkClass}>
            Patents
          </NavLink> */}

          {/* Analytics for admins only */}
          {canAccessAdmin && (
            <NavLink to="/analytics" className={navLinkClass}>
              Analytics
            </NavLink>
          )}

          {/* Local Mode Navigation */}
          {canAccessConfig && (
            <NavLink to="/config" className={navLinkClass}>
              Config
            </NavLink>
          )}
        </div>
      </div>

      {/* User Menu & Status */}
      <div className="flex items-center space-x-4">
        {/* Connection Status */}
        {user?.hasActiveSubscription && (
          <ConnectionStatus
            isLocalAvailable={isLocalAvailable}
            mode={isLocalAvailable ? 'local' : 'cloud'}
          />
        )}

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* User Menu */}
        <UserMenu user={user} onLogout={logout} />
      </div>
    </nav>
  );
}

function ConnectionStatus({ mode }: { isLocalAvailable: boolean; mode: 'local' | 'cloud' }) {
  return (
    <div className="flex items-center space-x-2 text-sm">
      <span className="text-muted-foreground">Mode:</span>
      <div
        className={`flex items-center space-x-1 px-2 py-1 rounded ${
          mode === 'local'
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
        }`}
      >
        {mode === 'local' ? (
          <>
            <div className="w-2 h-2 bg-current rounded-full" />
            <span>Local</span>
          </>
        ) : (
          <>
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              role="img"
              aria-label="Cloud mode"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z"
              />
            </svg>
            <span>Cloud</span>
          </>
        )}
      </div>
    </div>
  );
}

interface UserMenuProps {
  user: {
    email: string;
    name?: string;
    roles: string[];
    avatarUrl?: string;
  } | null;
  onLogout: () => void;
}

function UserMenu({ user, onLogout }: UserMenuProps) {
  const navigate = useNavigate();

  if (!user) return null;

  const hasAdminRole = user.roles.includes('admin') || user.roles.includes('superadmin');
  const hasSuperAdminRole = user.roles.includes('superadmin');

  // Get initials from name or email
  const getInitials = () => {
    if (user.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground hover:bg-accent/80 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent overflow-hidden"
          aria-label="User menu"
        >
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name || user.email}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xs font-medium">{getInitials()}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate('/account')}>
          <User className="mr-2 h-4 w-4" />
          <span>Account</span>
        </DropdownMenuItem>

        {hasAdminRole && (
          <DropdownMenuItem onClick={() => navigate('/admin')}>
            <Shield className="mr-2 h-4 w-4" />
            <span>Admin</span>
          </DropdownMenuItem>
        )}

        {hasSuperAdminRole && (
          <DropdownMenuItem onClick={() => navigate('/superadmin')}>
            <Zap className="mr-2 h-4 w-4" />
            <span>Super Admin</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
